import React from 'react';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import { SkeletonText } from '@components/atoms/Skeleton';
import { Client, ClientAccessSettingKey } from '@models/client.model';
import { useTranslation } from '@hooks/useTranslation';
import { formatDateOnly } from '@utils/formatDate';
import { useInviteClientUser } from '@hooks/api/clients/useInviteClientUser';
import { useRemoveClientAccess } from '@hooks/api/clients/useRemoveClientAccess';
import { useUpdateClientAccessSetting } from '@hooks/api/clients/useUpdateClientAccessSetting';
import { useState } from 'react';
import type en from '@locales/en';

// Sprint 29 (Addendum 10/10a typed-hook port, mechanical fix — architecture.md items 8-9).
// `useTranslation('clients')` is now concretely typed, so a dynamic `string`-typed key can no
// longer index into it implicitly — narrow the key type to the section's own keys instead.
type ClientsSectionKey = keyof (typeof en)['clients'];

interface ChecklistItem {
  key: ClientAccessSettingKey;
  labelKey: ClientsSectionKey;
}

interface ChecklistGroup {
  titleKey: ClientsSectionKey;
  items: ChecklistItem[];
}

// Sprint 25, story-128 (run 5). Exactly the keys the backend enforces
// (`backend/src/entities/client/client-access-settings.ts`), grouped per the UX doc's
// wireframe. `notifyTaskStatus`/`notifyDocuments` are deliberately absent — there is no CLIENT
// notification path on the server, so they must not appear as toggles here.
const CHECKLIST_GROUPS: ChecklistGroup[] = [
  {
    titleKey: 'groupWork',
    items: [
      { key: 'tasks', labelKey: 'item.tasks' },
      { key: 'scheduleDates', labelKey: 'item.scheduleDates' },
    ],
  },
  {
    titleKey: 'groupFiles',
    items: [
      { key: 'documents', labelKey: 'item.documents' },
      { key: 'evidence', labelKey: 'item.evidence' },
    ],
  },
  {
    titleKey: 'groupCommunication',
    items: [
      { key: 'notes', labelKey: 'item.notes' },
      { key: 'activity', labelKey: 'item.activity' },
      { key: 'incidents', labelKey: 'item.incidents' },
    ],
  },
  {
    titleKey: 'groupResources',
    items: [
      { key: 'materials', labelKey: 'item.materials' },
      { key: 'equipment', labelKey: 'item.equipment' },
    ],
  },
  {
    titleKey: 'groupMoney',
    items: [{ key: 'priceTotals', labelKey: 'item.priceTotals' }],
  },
];

// Keys whose ON state, per the server's dependency cascade
// (`applyClientAccessSettingCascade`), also turns `tasks` on.
const TASK_DEPENDENT_KEYS: ClientAccessSettingKey[] = ['scheduleDates', 'materials', 'equipment'];

interface ClientAccessTabProps {
  client: Client;
  /** OWNER/ADMIN/PROJECT_MANAGER — everyone else (e.g. SUPERINTENDENT) sees this read-only. */
  canManage: boolean;
}

export const ClientAccessTab: React.FC<ClientAccessTabProps> = ({ client, canManage }) => {
  const tClients = useTranslation('clients');
  const inviteMutation = useInviteClientUser();
  const removeAccessMutation = useRemoveClientAccess();
  const updateSettingMutation = useUpdateClientAccessSetting();
  const [resendSentAt, setResendSentAt] = useState<number | null>(null);
  const [dependencyNoteKey, setDependencyNoteKey] = useState<ClientAccessSettingKey | null>(null);

  const clientId = client.clientId as string;
  const linkedUser = client.linkedUser;
  const settings = client.accessSettings;

  const handleInvite = () => inviteMutation.mutate({ clientId, resend: false });

  const handleResend = () => {
    // Sprint 25, story-128 (run 5), Flow C. Client-side 30s debounce on top of the mutation's
    // own pending state, per the UX doc ("button label becomes 'Sent' with a check for that
    // window").
    inviteMutation.mutate({ clientId, resend: true });
    setResendSentAt(Date.now());
    setTimeout(() => setResendSentAt(null), 30_000);
  };

  const handleRemoveAccess = () => removeAccessMutation.mutate(clientId);

  const handleToggle = (key: ClientAccessSettingKey, value: boolean) => {
    updateSettingMutation.mutate({ clientId, key, value });
    if (value && TASK_DEPENDENT_KEYS.includes(key) && !settings?.tasks) {
      setDependencyNoteKey(key);
      setTimeout(() => setDependencyNoteKey(null), 4000);
    }
  };

  const loginStatusLabel = () => {
    if (!linkedUser) return tClients['loginStatusNone'] || 'No client login';
    if (linkedUser.status === 'ACTIVE') {
      return (tClients['loginStatusActive'] || 'Active · last sign-in {date}').replace(
        '{date}',
        formatDateOnly(linkedUser.lastSignInAt),
      );
    }
    return tClients['loginStatusInvited'] || 'Invited · not yet signed in';
  };

  if (!settings) {
    return (
      <Card className="mt-4">
        <SkeletonText lines={4} />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-4">
      <Card>
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {tClients['loginStatus'] || 'Client login'}
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  linkedUser?.status === 'ACTIVE'
                    ? 'bg-green-500'
                    : linkedUser
                      ? 'bg-yellow-500'
                      : 'bg-gray-300'
                }`}
              />
              <span className="text-sm text-gray-900 dark:text-gray-100">
                {loginStatusLabel()}
              </span>
            </div>
            {linkedUser?.email && (
              <p className="text-sm text-gray-500 dark:text-gray-300">{linkedUser.email}</p>
            )}
          </div>
          {canManage && (
            <div className="flex gap-2">
              {!linkedUser && client.email && (
                <Button
                  size="md"
                  variant="tertiary"
                  loading={inviteMutation.isPending}
                  onClick={handleInvite}>
                  {tClients['invite'] || 'Invite'}
                </Button>
              )}
              {linkedUser && linkedUser.status === 'INVITED' && (
                <Button
                  size="md"
                  variant="tertiary"
                  disabled={!!resendSentAt}
                  loading={inviteMutation.isPending}
                  onClick={handleResend}>
                  {resendSentAt
                    ? 'Sent'
                    : tClients['resendInvite'] || 'Resend invitation'}
                </Button>
              )}
              {linkedUser && (
                <Button
                  size="md"
                  variant="tertiary"
                  loading={removeAccessMutation.isPending}
                  onClick={handleRemoveAccess}>
                  {tClients['removeAccess'] || 'Remove access'}
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {tClients['whatClientCanSee'] || 'What this client can see'}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-300 mt-1">
          {tClients['alwaysVisible'] ||
            'Always visible: project name, status, dates, address, description, and the progress summary (completion, health, overdue and upcoming tasks, open incident count).'}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-300 mt-1">
          {tClients['defaultsNote'] ||
            'Tasks, schedule dates, activity feed and incidents are on by default; everything else is off until you turn it on.'}
        </p>
        {!canManage && (
          <p className="text-xs text-gray-500 dark:text-gray-300 mt-1 italic">
            {tClients['managerOnlyNote'] ||
              'Only Owner, Admin and Project Manager can change client access.'}
          </p>
        )}

        <div className="flex flex-col gap-4 mt-4">
          {CHECKLIST_GROUPS.map((group) => (
            <div key={group.titleKey}>
              <div className="text-xs font-semibold uppercase text-gray-400 mb-2">
                {tClients[group.titleKey]}
              </div>
              <div className="flex flex-col gap-2">
                {group.items.map((item) => (
                  <div key={item.key} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {tClients[item.labelKey]}
                      </span>
                      <ToggleSwitch
                        testId={`client-access-${item.key}`}
                        leftLabel="Off"
                        rightLabel="On"
                        checked={!!settings[item.key]}
                        disabled={!canManage || updateSettingMutation.isPending}
                        onChange={(value) => handleToggle(item.key, value)}
                      />
                    </div>
                    {dependencyNoteKey === item.key && (
                      <p className="text-xs text-blue-600">
                        {tClients['dependencyTasksOn'] ||
                          'Tasks turned on too — dates live on tasks.'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ClientAccessTab;
